import React, { useState, useEffect, useRef } from 'react';
import { PRIORITY_AR, STATUS_AR } from '../utils/labels';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import AttachmentsPanel from '../components/AttachmentsPanel';
import AssigneePicker from '../components/AssigneePicker';
import RepeatPicker, { repeatSummary } from '../components/RepeatPicker';
import TagPicker from '../components/TagPicker';
import { TagList } from '../components/TagBadge';
import { fmtDateBoth, fmtDateTimeBoth } from '../utils/dates';
import { hasPermission } from '../utils/roles';
import { toStages } from '../utils/workflowGraph';

const TRANSCRIPTION_STATUS_AR = { pending: 'بانتظار البدء', processing: 'قيد المعالجة', completed: 'مكتمل', failed: 'فشل' };

const STATUS_COLOR = {
  pending: 'var(--text-faint)', in_progress: 'var(--c-blue-500)', waiting_approval: 'var(--c-warning)',
  approved: 'var(--c-blue-500)', completed: 'var(--c-success)', revision: 'var(--c-coral)', cancelled: 'var(--text-muted)'
};
const ACTION_AR = {
  created: 'إنشاء مهمة', status_changed: 'تغيير حالة', commented: 'تعليق',
  updated: 'تعديل', client_approved: 'اعتماد العميل', client_revision: 'طلب مراجعة',
  time_logged: 'سجل وقتاً', steps_synced: 'توليد خطوات ناقصة'
};

function formatMinutes(m) {
  if (!m) return '0 دقيقة';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h === 0) return `${mm} دقيقة`;
  if (mm === 0) return `${h} ساعة`;
  return `${h}س ${mm}د`;
}

export default function TaskDetail() {
  const { id } = useParams();
  const { api, apiUpload, user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [assigningStep, setAssigningStep] = useState(null); // subtask id being assigned
  const audioInput = useRef(null);
  const [audioStep, setAudioStep] = useState(null); // خطوة تنتظر صوت الدرس قبل إغلاقها
  const [audioUploading, setAudioUploading] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [attachWorkflowId, setAttachWorkflowId] = useState('');
  const [newItem, setNewItem] = useState('');
  const [stepAssignees, setStepAssignees] = useState([]);
  const [payItems, setPayItems] = useState([]);

  const load = () => {
    api(`/tasks/${id}`)
      .then(t => {
        setTask(t);
        setEditForm({
          assignee_ids: (t.assignees || []).map(x => x.id),
          repeat: t.recurring_task && t.recurring_task.is_active ? {
            frequency: t.recurring_task.frequency,
            days_of_week: t.recurring_task.days_of_week || [],
            day_of_month: t.recurring_task.day_of_month,
            time_of_day: t.recurring_task.time_of_day,
          } : null,
          priority: t.priority,
          due_date: t.due_date?.split('T')[0] || '',
          start_date: t.start_date?.split('T')[0] || '',
          tag_ids: (t.tags || []).map(x => x.id),
          bucket: t.bucket || '',
          notes: t.notes || '',
          show_checklist_on_card: t.show_checklist_on_card ?? true,
          show_notes_on_card: t.show_notes_on_card ?? false,
        });
        setLoading(false);
      })
      .catch(() => navigate('/tasks'));
  };

  useEffect(() => { load(); api('/users').then(setUsers).catch(() => {}); api('/workflows').then(setWorkflows).catch(() => {}); }, [id]);
  useEffect(() => { api('/pay-items').then(setPayItems).catch(() => {}); }, []);

  const attachWorkflow = async () => {
    if (!attachWorkflowId) return;
    try {
      await api(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify({ workflow_id: Number(attachWorkflowId) }) });
      toast.success('رُبطت المهمة بالمسار وتولّدت خطواتها');
      setAttachWorkflowId('');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const syncSteps = async () => {
    try {
      const r = await api(`/tasks/${id}/sync-steps`, { method: 'POST' });
      toast.success(r.message || 'تمّت مزامنة خطوات المسار');
      load();
    } catch (err) { toast.error(err.message); }
  };

  /**
   * بند التسعير الذي يُحتسب بمقدار العمل لا يُقفل بلا مقدار — يُطلب من المنفِّذ
   * قبل الإكمال بدل أن يردّه الخادم بخطأ.
   */
  const ensureQuantity = async (t) => {
    const item = t?.pay_item;
    if (!item || !item.requires_quantity || t.pay_quantity !== null) return true;

    const answer = window.prompt(`مقدار العمل المنجز${item.unit ? ` بـ(${item.unit})` : ''} لبند «${item.name}»:`);
    if (answer === null) return false;

    const quantity = Number(answer);
    if (!Number.isFinite(quantity) || quantity < 0) {
      toast.error('أدخل رقماً صحيحاً');
      return false;
    }

    await api(`/tasks/${t.id}/pricing`, { method: 'PUT', body: JSON.stringify({ pay_quantity: quantity }) });
    return true;
  };

  const changeStatus = async (status) => {
    try {
      if (status === 'completed' && !(await ensureQuantity(task))) return;
      await api(`/tasks/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
      load();
    } catch (err) { toast.error(err.message); }
  };

  const savePricing = async (patch) => {
    try {
      await api(`/tasks/${id}/pricing`, { method: 'PUT', body: JSON.stringify(patch) });
      toast.success('تم حفظ التسعير');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    await api(`/tasks/${id}/comments`, { method: 'POST', body: JSON.stringify({ comment, is_client_visible: false }) });
    setComment('');
    load();
  };

  const saveEdit = async () => {
    await api(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(editForm) });
    setEditing(false);
    load();
  };

  const approve = async (action, notes) => {
    await api(`/tasks/${id}/approve`, { method: 'POST', body: JSON.stringify({ action, notes }) });
    load();
  };

  const deleteTask = async () => {
    if (!window.confirm(`هل أنت متأكد من حذف المهمة "${task.title}"؟ سيتم حذف جميع التعليقات والمرفقات والأوقات المسجّلة والمهام الفرعية.`)) return;
    try {
      await api(`/tasks/${id}`, { method: 'DELETE' });
      toast.success('تم حذف المهمة');
      navigate('/tasks');
    } catch (err) { toast.error(err.message); }
  };

  const [timeForm, setTimeForm] = useState({ minutes: '', note: '' });

  const logTime = async () => {
    const minutes = parseInt(timeForm.minutes);
    if (!minutes || minutes < 1) return;
    try {
      await api(`/tasks/${id}/time`, { method: 'POST', body: JSON.stringify({ minutes, note: timeForm.note || null }) });
      setTimeForm({ minutes: '', note: '' });
      load();
    } catch (err) { toast.error(err.message); }
  };

  const assignSubtask = async (subtaskId, assigneeIds) => {
    await api(`/tasks/${subtaskId}`, { method: 'PUT', body: JSON.stringify({ assignee_ids: assigneeIds }) });
    setAssigningStep(null);
    load();
  };

  const changeSubtaskStatus = async (subtaskId, status) => {
    try {
      const subtask = (task?.subtasks || []).find(s => s.id === subtaskId);
      // الخطوة التي تشترط صوت الدرس لا تُغلق بدونه — يُطلب الملفّ قبل الإغلاق
      if (['completed', 'approved'].includes(status) && subtask?.requires_audio && !subtask.has_audio) {
        setAudioStep({ id: subtaskId, status });
        audioInput.current?.click();
        return;
      }
      if (status === 'completed' && !(await ensureQuantity(subtask))) return;
      await api(`/tasks/${subtaskId}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
      load();
    } catch (err) { toast.error(err.message); }
  };

  const uploadLessonAudio = async (file) => {
    const pending = audioStep;
    setAudioStep(null);
    if (!file || !pending) return;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['mp3', 'm4a'].includes(ext)) {
      toast.error('ملفّ صوت الدرس يجب أن يكون mp3 أو m4a');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('files[]', file);
      setAudioUploading(0);
      await apiUpload(`/tasks/${pending.id}/attachments`, fd, setAudioUploading);
      const subtask = (task?.subtasks || []).find(s => s.id === pending.id);
      if (pending.status === 'completed' && !(await ensureQuantity(subtask))) { load(); return; }
      await api(`/tasks/${pending.id}/status`, { method: 'PUT', body: JSON.stringify({ status: pending.status }) });
      toast.success('أُرفق الصوت وأُغلقت الخطوة — بدأ التفريغ آلياً');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAudioUploading(null);
    }
  };

  const addChecklistItem = async () => {
    const title = newItem.trim();
    if (!title) return;
    try {
      await api(`/tasks/${id}/checklist`, { method: 'POST', body: JSON.stringify({ title }) });
      setNewItem('');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const toggleChecklistItem = async (item) => {
    try {
      await api(`/tasks/${id}/checklist/${item.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_completed: !item.is_completed }),
      });
      load();
    } catch (err) { toast.error(err.message); }
  };

  const removeChecklistItem = async (item) => {
    if (!window.confirm(`حذف العنصر "${item.title}"؟`)) return;
    try {
      await api(`/tasks/${id}/checklist/${item.id}`, { method: 'DELETE' });
      load();
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <div className="loader">جاري التحميل...</div>;
  if (!task) return null;

  const workflowSteps = task.workflow?.steps || [];
  const isWorkflowTask = workflowSteps.length > 0 && task.subtasks?.length > 0;
  const canManageSteps = ['admin', 'supervisor'].includes(user.role);
  const canPrice = hasPermission(user, 'payroll.manage');
  const isDoer = (task.assignees || []).some(a => a.id === user.id) || task.assigned_to === user.id;
  const canUseChecklist = user.role !== 'client';
  const checklist = task.checklist_items || [];
  const doneCount = checklist.filter(i => i.is_completed).length;
  const progressPct = checklist.length ? Math.round((doneCount / checklist.length) * 100) : 0;
  const taskSteps = workflowSteps.filter(s => (s.type || 'task') !== 'fork');
  const missingSteps = taskSteps.filter(step => !task.subtasks?.some(st => st.current_step === step.order));
  // المراحل: خطوات المرحلة الواحدة متوازية تُفتح معاً
  const stages = toStages(taskSteps);

  return (
    <div>
      <button className="btn btn-outline btn-sm" onClick={() => navigate('/tasks')} style={{ marginBottom: 16 }}>→ العودة للمهام</button>
      <input
        ref={audioInput}
        type="file"
        accept=".mp3,.m4a,audio/mpeg,audio/mp4,audio/x-m4a"
        style={{ display: 'none' }}
        onChange={e => { uploadLessonAudio(e.target.files?.[0]); e.target.value = ''; }}
      />
      {task.transcription && (
        <div className="card" style={{ marginBottom: 16, padding: '10px 14px', fontSize: 13 }}>
          🎙 تفريغ الدرس #{task.transcription.id} — {TRANSCRIPTION_STATUS_AR[task.transcription.status] || task.transcription.status}
          {task.transcription.status === 'processing' && task.transcription.progress != null ? ` (${task.transcription.progress}%)` : ''}
          {' '}<button className="btn btn-outline btn-sm" onClick={() => navigate(`/transcription?id=${task.transcription.id}`)}>فتح</button>
        </div>
      )}

      <div className="task-detail">
        <div>
          {/* Header Card */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ marginBottom: 8 }}>{task.title}</h2>
                <span className={`badge badge-${task.status}`}>{STATUS_AR[task.status]}</span>
                <span className={`badge badge-${task.priority}`} style={{ marginRight: 8 }}>{PRIORITY_AR[task.priority] || task.priority}</span>
                {task.workflow && <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 8 }}>📋 {task.workflow.name}</span>}
                {task.bucket && <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 8 }}>🗂️ {task.bucket}</span>}
                <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 8 }}>
                  🔁 {task.recurring_task?.is_active ? repeatSummary(task.recurring_task) : 'لا يتكرر'}
                </span>
              </div>
              {['admin', 'supervisor'].includes(user.role) && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-sm btn-outline" onClick={() => setEditing(!editing)}>
                    {editing ? 'إلغاء' : 'تعديل'}
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={deleteTask}>
                    حذف
                  </button>
                </div>
              )}
            </div>

            {task.description && <p style={{ marginTop: 12, color: 'var(--text-body)' }}>{task.description}</p>}

            {editing && (
              <div style={{ marginTop: 16, padding: 16, background: 'var(--surface-alt)', borderRadius: 8 }}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">المكلَّفون</label>
                    <AssigneePicker
                      value={editForm.assignee_ids || []}
                      onChange={ids => setEditForm(f => ({ ...f, assignee_ids: ids }))}
                      users={users}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">الأولوية</label>
                    <select className="form-select" value={editForm.priority} onChange={e => setEditForm(f => ({ ...f, priority: e.target.value }))}>
                      <option value="low">منخفضة</option><option value="medium">متوسطة</option>
                      <option value="high">عالية</option><option value="urgent">عاجلة</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">تاريخ البدء (اختياري)</label>
                    <input className="form-input" type="date" value={editForm.start_date} onChange={e => setEditForm(f => ({ ...f, start_date: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">تاريخ التسليم</label>
                    <input className="form-input" type="date" value={editForm.due_date} onChange={e => setEditForm(f => ({ ...f, due_date: e.target.value }))} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">الوسوم</label>
                  <TagPicker value={editForm.tag_ids || []} onChange={ids => setEditForm(f => ({ ...f, tag_ids: ids }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">🔁 تكرار</label>
                  <RepeatPicker value={editForm.repeat} onChange={r => setEditForm(f => ({ ...f, repeat: r }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">🗂️ المستودع</label>
                  <input
                    className="form-input"
                    placeholder="مثال: العمليات"
                    value={editForm.bucket}
                    onChange={e => setEditForm(f => ({ ...f, bucket: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <div className="checklist-head">
                    <label className="form-label" style={{ margin: 0 }}>📝 ملاحظات</label>
                    <label className="board-toggle">
                      <input
                        type="checkbox"
                        checked={editForm.show_notes_on_card}
                        onChange={e => setEditForm(f => ({ ...f, show_notes_on_card: e.target.checked }))}
                      />
                      عرض في طريقة عرض اللوحة
                    </label>
                  </div>
                  <textarea
                    className="form-textarea"
                    placeholder="اكتب وصفاً أو أضف ملاحظات هنا"
                    value={editForm.notes}
                    onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  />
                </div>
                <label className="board-toggle" style={{ marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={editForm.show_checklist_on_card}
                    onChange={e => setEditForm(f => ({ ...f, show_checklist_on_card: e.target.checked }))}
                  />
                  عرض قائمة الاختيار في طريقة عرض اللوحة
                </label>
                <button className="btn btn-primary btn-sm" onClick={saveEdit}>حفظ</button>
              </div>
            )}

            {/* Actions */}
            {!isWorkflowTask && (
              <div style={{ marginTop: 20, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {task.status === 'pending' && <button className="btn btn-primary btn-sm" onClick={() => changeStatus('in_progress')}>بدء العمل</button>}
                {task.status === 'in_progress' && <button className="btn btn-warning btn-sm" onClick={() => changeStatus('waiting_approval')}>إرسال للاعتماد</button>}
                {task.status === 'in_progress' && <button className="btn btn-success btn-sm" onClick={() => changeStatus('completed')}>إكمال</button>}
                {task.status === 'waiting_approval' && ['admin', 'supervisor'].includes(user.role) && (
                  <>
                    <button className="btn btn-success btn-sm" onClick={() => approve('approved')}>اعتماد</button>
                    <button className="btn btn-danger btn-sm" onClick={() => { const n = prompt('ملاحظات المراجعة:'); if (n) approve('revision', n); }}>إرجاع للمراجعة</button>
                  </>
                )}
                {task.status === 'revision' && <button className="btn btn-primary btn-sm" onClick={() => changeStatus('in_progress')}>إعادة العمل</button>}
                {['admin', 'supervisor'].includes(user.role) && task.assigned_to && task.assigned_to !== user.id && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={async () => {
                      const note = prompt('ملاحظة التذكير (اختيارية):') || '';
                      try {
                        const r = await api(`/tasks/${id}/remind`, { method: 'POST', body: JSON.stringify({ note }) });
                        toast.success(r.message || 'تم إرسال التذكير');
                      } catch (e) {
                        toast.error(e.message || 'فشل إرسال التذكير');
                      }
                    }}
                  >إرسال تذكير</button>
                )}
              </div>
            )}
          </div>

          {/* Checklist + Notes */}
          {canUseChecklist && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="checklist-head" style={{ marginBottom: 12 }}>
                <div className="card-title" style={{ margin: 0 }}>
                  ☑️ قائمة الاختيار
                  {checklist.length > 0 && (
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 400, marginRight: 6 }}>
                      (اكتمل {doneCount} من {checklist.length} من العناصر)
                    </span>
                  )}
                </div>
                {checklist.length > 0 && (
                  <span className="checklist-progress" title={`${progressPct}%`}>
                    <span style={{ width: `${progressPct}%` }} />
                  </span>
                )}
              </div>

              {checklist.map(item => (
                <div key={item.id} className="checklist-row">
                  <input
                    type="checkbox"
                    checked={!!item.is_completed}
                    onChange={() => toggleChecklistItem(item)}
                    title={item.is_completed && item.completer ? `أنجزه ${item.completer.name}` : 'وضع علامة إنجاز'}
                  />
                  <span style={{ flex: 1, textDecoration: item.is_completed ? 'line-through' : 'none', color: item.is_completed ? 'var(--text-muted)' : 'inherit' }}>
                    {item.title}
                  </span>
                  <button type="button" className="checklist-remove" title="حذف العنصر" onClick={() => removeChecklistItem(item)}>✕</button>
                </div>
              ))}

              <input
                className="form-input"
                placeholder="إضافة عنصر ثم Enter"
                value={newItem}
                onChange={e => setNewItem(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addChecklistItem(); } }}
              />

              {task.notes && (
                <div style={{ marginTop: 16 }}>
                  <div className="card-title" style={{ marginBottom: 6 }}>📝 ملاحظات</div>
                  <p style={{ whiteSpace: 'pre-wrap', color: 'var(--text)', margin: 0 }}>{task.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Workflow Progress */}
          {isWorkflowTask && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <div className="card-title">مراحل المسار — {task.workflow.name}</div>
                {canManageSteps && missingSteps.length > 0 && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={syncSteps}
                    title="ينشئ المهام الفرعية للخطوات التي أُضيفت للمسار بعد إنشاء المهمة ويعيد فتحها"
                  >توليد الخطوات الناقصة ({missingSteps.length})</button>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {stages.map((stageSteps, stageIndex) => (
                  <div key={stageIndex} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>المرحلة {stageIndex + 1}</span>
                      {stageSteps.length > 1 && (
                        <span className="badge badge-info" style={{ fontSize: 10 }}>⑂ {stageSteps.length} خطوات متوازية</span>
                      )}
                    </div>

                    {stageSteps.map((step, i) => {
                      const subtask = task.subtasks?.find(st => st.current_step === step.order);
                      const isDone = subtask?.status === 'completed';
                      const isActive = subtask?.status === 'in_progress' || subtask?.status === 'waiting_approval';
                      const isPending = subtask?.status === 'pending' || !subtask;

                      return (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '10px 14px', borderRadius: 8,
                          background: isDone ? 'var(--c-success-soft)' : isActive ? 'var(--c-blue-100)' : 'var(--surface-alt)',
                          border: `1px solid ${isDone ? 'var(--c-success)' : isActive ? 'var(--c-blue-300)' : 'var(--border)'}`
                        }}>
                          {/* Step indicator */}
                          <div style={{
                            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: isDone ? 'var(--c-success)' : isActive ? 'var(--c-blue-500)' : 'var(--border-strong)',
                            color: '#fff', fontSize: 13, fontWeight: 600
                          }}>
                            {isDone ? '✓' : step.order + 1}
                          </div>

                          {/* Step info */}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 500, fontSize: 14, color: isDone ? 'var(--c-success)' : isActive ? 'var(--text-strong)' : 'var(--text-body)' }}>
                              {step.name}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                              {subtask ? STATUS_AR[subtask.status] : 'في الانتظار'}
                              {subtask?.requires_audio && (
                                <span style={{ marginInlineStart: 8 }}>
                                  {subtask.has_audio ? '🎙 صوت الدرس مرفق' : '🎙 تشترط إرفاق صوت الدرس (mp3/m4a)'}
                                </span>
                              )}
                              {subtask?.requires_audio && audioUploading !== null && audioStep === null && (
                                <span style={{ marginInlineStart: 8 }}>جارِ الرفع {audioUploading}%</span>
                              )}
                            </div>
                          </div>

                          {/* Step Actions */}
                          {subtask && (user.id === subtask.assigned_to || ['admin', 'supervisor'].includes(user.role)) && (
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {subtask.status === 'pending' && (
                                <button className="btn btn-primary btn-sm" style={{ fontSize: 11, padding: '3px 8px' }} onClick={e => { e.stopPropagation(); changeSubtaskStatus(subtask.id, 'in_progress'); }}>بدء العمل</button>
                              )}
                              {subtask.status === 'in_progress' && (
                                <>
                                  {step.type !== 'approval' && (
                                    <button className="btn btn-success btn-sm" style={{ fontSize: 11, padding: '3px 8px' }} onClick={e => { e.stopPropagation(); changeSubtaskStatus(subtask.id, 'completed'); }}>أكملت المهمة</button>
                                  )}
                                  {step.type === 'approval' && (
                                    <button className="btn btn-warning btn-sm" style={{ fontSize: 11, padding: '3px 8px' }} onClick={e => { e.stopPropagation(); changeSubtaskStatus(subtask.id, 'waiting_approval'); }}>إرسال للاعتماد</button>
                                  )}
                                </>
                              )}
                              {subtask.status === 'waiting_approval' && ['admin', 'supervisor'].includes(user.role) && (
                                <>
                                  <button className="btn btn-success btn-sm" style={{ fontSize: 11, padding: '3px 8px' }} onClick={e => { e.stopPropagation(); changeSubtaskStatus(subtask.id, 'completed'); }}>اعتماد ✓</button>
                                  <button className="btn btn-danger btn-sm" style={{ fontSize: 11, padding: '3px 8px' }} onClick={e => { e.stopPropagation(); changeSubtaskStatus(subtask.id, 'revision'); }}>مراجعة</button>
                                </>
                              )}
                              {subtask.status === 'revision' && (
                                <button className="btn btn-primary btn-sm" style={{ fontSize: 11, padding: '3px 8px' }} onClick={e => { e.stopPropagation(); changeSubtaskStatus(subtask.id, 'in_progress'); }}>إعادة العمل</button>
                              )}
                            </div>
                          )}

                          {/* Assignee */}
                          <div style={{ textAlign: 'left', minWidth: 130 }}>
                            {assigningStep === subtask?.id ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
                                <AssigneePicker
                                  value={stepAssignees}
                                  onChange={setStepAssignees}
                                  users={users}
                                />
                                <div style={{ display: 'flex', gap: 6 }}>
                                  <button className="btn btn-sm btn-primary" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => assignSubtask(subtask.id, stepAssignees)}>حفظ</button>
                                  <button className="btn btn-sm btn-outline" style={{ padding: '2px 6px', fontSize: 11 }} onClick={() => setAssigningStep(null)}>✕</button>
                                </div>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 12, color: subtask?.assignee?.name ? 'var(--text-body)' : 'var(--text-faint)' }}>
                                  👤 {(subtask?.assignees || []).length > 0
                                    ? subtask.assignees.map(a => a.name).join('، ')
                                    : (subtask?.assignee?.name || 'غير محدد')}
                                </span>
                                {subtask && ['admin', 'supervisor'].includes(user.role) && (
                                  <button
                                    className="btn btn-sm"
                                    style={{ padding: '2px 6px', fontSize: 11, background: 'transparent', border: '1px solid var(--border-strong)', borderRadius: 4 }}
                                    onClick={e => { e.stopPropagation(); setStepAssignees((subtask.assignees || []).map(a => a.id)); setAssigningStep(subtask.id); }}
                                  >✏️</button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comments */}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 12 }}>التعليقات</div>
            {task.comments?.map(c => (
              <div key={c.id} className="comment">
                <div className="comment-header">
                  <span className="comment-author">
                    {c.user?.name}
                    <span className={`badge badge-${c.user?.role === 'client' ? 'approved' : 'in_progress'}`} style={{ fontSize: 10, marginRight: 4 }}>
                      {c.user?.role === 'client' ? 'عميل' : 'فريق'}
                    </span>
                  </span>
                  <span className="comment-date">{fmtDateTimeBoth(c.created_at)}</span>
                </div>
                <div className="comment-text">{c.comment}</div>
              </div>
            ))}
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <textarea className="form-textarea" style={{ minHeight: 60 }} placeholder="أضف تعليقاً..." value={comment} onChange={e => setComment(e.target.value)} />
              <button className="btn btn-primary btn-sm" onClick={addComment} disabled={!comment.trim()} style={{ alignSelf: 'flex-end' }}>إرسال</button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-title" style={{ marginBottom: 12 }}>التفاصيل</div>
            <div className="task-meta">
              <div className="meta-item">
                <div className="meta-label">المكلَّفون</div>
                <div className="meta-value">
                  {(task.assignees || []).length > 0
                    ? task.assignees.map(a => a.name).join('، ')
                    : (task.assignee?.name || 'غير محدد')}
                </div>
              </div>
              <div className="meta-item">
                <div className="meta-label">العميل</div>
                <div className="meta-value">{task.client?.name || '—'}</div>
              </div>
              <div className="meta-item">
                <div className="meta-label">المسار</div>
                <div className="meta-value">{task.workflow?.name || 'مهمة يدوية'}</div>
                {!task.workflow && !task.parent_task_id && workflows.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <select className="form-select" style={{ fontSize: 12, padding: '2px 6px' }} value={attachWorkflowId} onChange={e => setAttachWorkflowId(e.target.value)}>
                      <option value="">ربط بمسار...</option>
                      {workflows.filter(w => w.is_active !== false).map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                    <button className="btn btn-sm btn-outline" disabled={!attachWorkflowId} onClick={attachWorkflow} title="يولّد خطوات المسار كمهام فرعية">ربط</button>
                  </div>
                )}
              </div>
              <div className="meta-item">
                <div className="meta-label">الأولوية</div>
                <div className="meta-value"><span className={`badge badge-${PRIORITY_AR[task.priority] || task.priority}`}>{PRIORITY_AR[task.priority] || task.priority}</span></div>
              </div>
              <div className="meta-item">
                <div className="meta-label">تاريخ الإنشاء</div>
                <div className="meta-value">{fmtDateBoth(task.created_at)}</div>
              </div>
              <div className="meta-item">
                <div className="meta-label">الموعد النهائي</div>
                <div className="meta-value" style={{ color: task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed' ? 'var(--danger)' : undefined }}>
                  {task.due_date ? fmtDateBoth(task.due_date) : '—'}
                </div>
              </div>
              {task.start_date && (
                <div className="meta-item">
                  <div className="meta-label">تاريخ البدء</div>
                  <div className="meta-value">{fmtDateBoth(task.start_date)}</div>
                </div>
              )}
              {task.tags?.length > 0 && (
                <div className="meta-item">
                  <div className="meta-label">الوسوم</div>
                  <div className="meta-value"><TagList tags={task.tags} /></div>
                </div>
              )}
              {isWorkflowTask && (
                <div className="meta-item">
                  <div className="meta-label">التقدم</div>
                  <div className="meta-value">
                    {task.subtasks?.filter(s => s.status === 'completed').length} / {task.subtasks?.length} مراحل
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* تسعير العمل */}
          {(canPrice || task.pay_item) && !isWorkflowTask && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-title" style={{ marginBottom: 12 }}>💰 تسعير العمل</div>

              {canPrice ? (
                <div className="form-group">
                  <label className="form-label">البند</label>
                  <select
                    className="form-input"
                    value={task.pay_item_id || ''}
                    onChange={e => savePricing({ pay_item_id: e.target.value ? Number(e.target.value) : null })}
                  >
                    <option value="">— بلا تسعير —</option>
                    {payItems.map(i => (
                      <option key={i.id} value={i.id}>
                        {i.name}{i.unit ? ` / ${i.unit}` : ''} — {Number(i.unit_price)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="meta-item">
                  <div className="meta-label">البند</div>
                  <div className="meta-value">{task.pay_item.name}</div>
                </div>
              )}

              {task.pay_item && (
                <>
                  <div className="form-group">
                    <label className="form-label">
                      المقدار المنجز{task.pay_item.unit ? ` (${task.pay_item.unit})` : ''}
                    </label>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="0.01"
                      defaultValue={task.pay_quantity ?? ''}
                      disabled={!canPrice && !isDoer}
                      onBlur={e => {
                        const v = e.target.value === '' ? null : Number(e.target.value);
                        if (String(v) !== String(task.pay_quantity ?? null)) savePricing({ pay_quantity: v });
                      }}
                    />
                  </div>
                  <div className="meta-item">
                    <div className="meta-label">المستحق</div>
                    <div className="meta-value" style={{ fontWeight: 700 }}>
                      {(Number(task.pay_quantity ?? 0) * Number(task.pay_unit_price ?? task.pay_item.unit_price)).toLocaleString('ar-EG', { maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  {task.pay_item.requires_quantity && task.pay_quantity === null && (
                    <div style={{ fontSize: 12, color: 'var(--c-warning)' }}>
                      أدخل المقدار قبل إكمال المهمة.
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                    المبلغ يُثبَّت في كشف الشهر لحظة الإكمال، ولا يتأثّر بتغيير الأسعار بعدها.
                  </div>
                </>
              )}
            </div>
          )}

          {/* Attachments */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-title" style={{ marginBottom: 12 }}>📎 المرفقات</div>
            <AttachmentsPanel
              basePath={`/tasks/${task.id}/attachments`}
              attachments={task.attachments || []}
              onChange={load}
              canEdit={['admin', 'team'].includes(user?.role)}
            />
          </div>

          {/* Time Tracking */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-title" style={{ marginBottom: 12 }}>⏱ تتبع الوقت</div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--text-muted)' }}>المسجّل:</span>
                <strong>{formatMinutes(task.logged_minutes || 0)}</strong>
              </div>
              {task.estimated_minutes > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 4 }}>
                    <span style={{ color: 'var(--text-muted)' }}>المقدّر:</span>
                    <span>{formatMinutes(task.estimated_minutes)}</span>
                  </div>
                  <div style={{ height: 6, background: 'var(--gray-100)', borderRadius: 3, marginTop: 8 }}>
                    <div style={{
                      height: '100%', borderRadius: 3,
                      width: `${Math.min(100, (task.logged_minutes / task.estimated_minutes) * 100)}%`,
                      background: task.logged_minutes > task.estimated_minutes ? 'var(--danger)' : 'var(--primary)',
                      transition: 'width .3s'
                    }} />
                  </div>
                </>
              )}
            </div>
            {['admin', 'team'].includes(user?.role) && (
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  className="form-input"
                  type="number"
                  placeholder="دقائق"
                  style={{ width: 80 }}
                  value={timeForm.minutes}
                  onChange={e => setTimeForm(f => ({ ...f, minutes: e.target.value }))}
                />
                <input
                  className="form-input"
                  placeholder="ملاحظة (اختياري)"
                  value={timeForm.note}
                  onChange={e => setTimeForm(f => ({ ...f, note: e.target.value }))}
                />
                <button className="btn btn-primary btn-sm" onClick={logTime} disabled={!timeForm.minutes}>إضافة</button>
              </div>
            )}
            {task.time_entries?.length > 0 && (
              <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                {task.time_entries.slice(0, 5).map(e => (
                  <div key={e.id} style={{ fontSize: 12, padding: '4px 0', display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
                    <span>{e.user?.name} • {formatMinutes(e.minutes)}</span>
                    <span>{fmtDateBoth(e.logged_on || e.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Activity Log */}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 12 }}>سجل النشاط</div>
            {task.logs?.map(l => (
              <div key={l.id} className="log-item">
                <div className="log-dot"></div>
                <div>
                  <div style={{ fontSize: 13 }}><strong>{l.user?.name}</strong> — {ACTION_AR[l.action] || l.action}</div>
                  <div className="log-time">{fmtDateTimeBoth(l.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
